import CloseIcon from '@mui/icons-material/Close';
import {
    AppBar,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    Link,
    Toolbar,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { Box, Container, Stack } from '@mui/system';

import { useTheme } from '@emotion/react';
import { InfoOutlined } from '@mui/icons-material';
import { useQueryClient } from '@tanstack/react-query';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import UserTag from '../../components/UserTag';
import { getCalendarSettingAssignee } from '../../redux/Calendar/action';
import { acceptDeclineAppointment } from '../../redux/Dashboard/action';
import { appointmentKeys } from '../../services/business/appointment';
import { APPOINTMENT_REQUEST_GENERATED_PLATFORM, USER_CREATED_FROM } from '../../utility/Constant';
import { truncateText } from '../../utility/helpers/displayHelper';
import { formatPhoneNumber } from '../Common/FormatPhoneNumber';
import SuccessMessageModal from '../Common/SuccessMessage';
import GuestCustomerInfoModal from '../Customers/GuestCustomers/GuestCustomerInfoModal';
import '../Estimates/Estimates.css';
import FormatNumber from './../../components/FormatNumber/FormatNumber';
import AddAssigneeModal from './AddAssigneeModal';
import EditEstimatePickUpModal from './EditEstimatePickUpModal';
import RequestProposeTimeModel from './RequestProposeTimeModel';
import './modal.css';

const AppointmentRequestModal = ({
    isProposeTime = true,
    showNew = true,
    open,
    handleClose,
    appointmentData,
    id,
    handleCancelAppointment,
    apiCall,
    fetchAppointmentDetails,
    showDeclineBtn = true,
    showAcceptBtn = true,
    showProposeBtn = true,
    tab = null,
    /** Set by parent for Accepted tab cancelled requests only: 'shop' | 'customer' | null */
    cancellationBannerType = null,
    declineBannerType = null,
}) => {
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    const [showLoader, setShowLoader] = React.useState(false);
    const [showModel, setShowModel] = React.useState(false);
    const [openSuccessModal, setOpenSuccessModal] = React.useState(false);
    const [successMessage, setSuccessMessage] = React.useState('');
    const [showAssignee, setShowAssignee] = React.useState(false);
    const [showEstimatePickUp, setShowEstimatePickUp] = React.useState(false);
    const [estimatePickUpDate, setEstimatePickUpDate] = React.useState(null);
    const navigate = useNavigate();
    const theme = useTheme();
    const [defaultcalendarData, setDefaultCalendarData] = useState({});
    const [openGuestModal, setOpenGuestModal] = React.useState(false);

    useEffect(() => {
        fetchDefaultSetting();
    }, []);

    const fetchDefaultSetting = async () => {
        const resp = await dispatch(getCalendarSettingAssignee());
        if (resp) {
            setDefaultCalendarData(resp?.data);
        }
    };

    const handleDecline = async () => {
        setShowLoader(true);
        try {
            const payload = { status: 'decline' };
            const data = await dispatch(acceptDeclineAppointment(id, payload));
            if (data) {
                setOpenSuccessModal(true);
                setSuccessMessage('Appointment request was successfully declined');
                apiCall();
                handleClose();
            }
        } finally {
            setShowLoader(false);
        }
    };

    const handleAccept = async () => {
        if (defaultcalendarData?.skipAssigneeSelection) {
            setShowLoader(true);
            try {
                const payload = {
                    status: 'accept',
                    assignee: defaultcalendarData?.defaultAssigneeId,
                };
                const data = await dispatch(acceptDeclineAppointment(id, payload));
                if (data) {
                    queryClient.invalidateQueries({ queryKey: appointmentKeys.all, refetchType: 'all' });
                    setOpenSuccessModal(true);
                    setSuccessMessage('Appointment request accepted');
                    apiCall();
                    handleClose();
                }
            } finally {
                setShowLoader(false);
            }
        } else {
            setShowAssignee(true);
        }
    };

    const handleProposeTime = () => setShowModel(true);
    const handleClosePropose = () => setShowModel(false);
    const handleCloseAssigneeModal = () => setShowAssignee(false);
    const handleCloseEsatimatePickUpModal = () => setShowEstimatePickUp(false);

    const handleCancle = () => {
        handleCancelAppointment();
        setEstimatePickUpDate(null);
    };

    return (
        <Box>
            {/*
              ── Main Dialog (replaces Modal + inner Box) ──────────────────────────────
              PaperProps mirrors the old inner Box's sizing, border-radius, and flex layout.
              `scroll="paper"` keeps DialogContent scrollable while Title and Actions stay fixed.
            */}
            <Dialog
                open={open}
                onClose={handleCancelAppointment}
                aria-labelledby="appointment-dialog-title"
                scroll="paper"
                PaperProps={{
                    sx: {
                        width: { xs: '95vw', sm: '90vw', md: '900px' },
                        maxWidth: '900px',
                        maxHeight: '95dvh',
                        borderRadius: '8px',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                        m: 0,
                    },
                }}
            >
                {/* ── DialogTitle renders the AppBar header ── */}
                <DialogTitle id="appointment-dialog-title" sx={{ p: 0, flexShrink: 0 }}>
                    <AppBar
                        className="modalHeader"
                        variant="elevation"
                        sx={{
                            height: '64px',
                            borderRadius: '8px 8px 0px 0px !important',
                            paddingRight: '0px !important',
                            boxShadow: 'none',
                            position: 'relative',
                        }}
                    >
                        <Toolbar variant="regular">
                            <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'} width={'100%'}>
                                <Box width={'80%'} display={'flex'} flexDirection={'row'} justifyContent={'start'} alignItems={'center'} gap={1.5}>
                                    <Typography variant="h6" fontWeight={500} style={{ color: '#000' }}>
                                        Appointment Request
                                    </Typography>
                                    <UserTag
                                        type={
                                            appointmentData?.customer?.createdFrom === USER_CREATED_FROM.SHOP_GUEST
                                                ? 1
                                                : appointmentData?.createdPlatform === APPOINTMENT_REQUEST_GENERATED_PLATFORM.ONLINE_WEB
                                                  ? 2
                                                  : appointmentData?.createdPlatform === APPOINTMENT_REQUEST_GENERATED_PLATFORM.CUSTOMER_APP
                                                    ? 3
                                                    : 3
                                        }
                                    />
                                </Box>
                                <Stack direction={'row'} spacing={0}>
                                    <div onClick={handleCancle} className="appointmentModal" style={{ cursor: 'pointer', height: '32px' }}>
                                        <CloseIcon style={{ fontSize: 32, color: '#707070' }} />
                                    </div>
                                </Stack>
                            </Stack>
                        </Toolbar>
                    </AppBar>
                </DialogTitle>

                {/* ── DialogContent is the scrollable body ── */}
                <DialogContent sx={{ p: 0, overflowY: 'auto', scrollbarWidth: 'thin' }}>
                    <Container gap={2} disableGutters style={{ marginTop: '0' }}>
                        {(cancellationBannerType === 'shop' || cancellationBannerType === 'customer') && (
                            <div style={{ padding: '16px 24px', borderRadius: '0!important', borderBottom: '1px solid #C4C4C4' }}>
                                <Stack direction={'row'} width={'100%'} padding={'0 16px'} height={'52px'} alignItems={'center'} gap={1} bgcolor="#D32F2F">
                                    <InfoOutlined sx={{ color: '#fff' }} />
                                    <Typography variant="body1" fontWeight={400} fontSize={'16px'} color={'#fff'}>
                                        {cancellationBannerType === 'shop' ? 'Appointment Cancelled by Shop' : 'Appointment Cancelled by Customer'}
                                    </Typography>
                                </Stack>
                            </div>
                        )}
                        {(tab === 2 || tab === 3 || tab === 4) && (
                            <div style={{ padding: '16px 24px', borderRadius: '0!important', borderBottom: '1px solid #C4C4C4' }}>
                                <Stack
                                    direction={'row'}
                                    width={'100%'}
                                    padding={'0 16px'}
                                    height={'52px'}
                                    alignItems={'center'}
                                    gap={1}
                                    bgcolor={tab === 3 || tab === 4 ? '#D32F2F' : '#006CDD'}
                                >
                                    <InfoOutlined sx={{ color: '#fff' }} />
                                    <Typography variant="body1" fontWeight={400} fontSize={'16px'} color={'#fff'}>
                                        {tab === 2
                                            ? 'New Drop Off Sent to Customer'
                                            : tab === 3 && declineBannerType === 'customer'
                                              ? 'New Time Proposal Declined by Customer'
                                              : tab === 3
                                                ? 'Appointment Declined by Shop'
                                                : tab === 4
                                                  ? 'Appointment Expired (not accepted within 24 hours)'
                                                  : ''}
                                    </Typography>
                                </Stack>
                            </div>
                        )}

                        <Grid container style={{ backgroundColor: '#F5F5F7' }}>
                            {/* ─── Left column ─── */}
                            <Grid
                                container
                                item
                                xs={12}
                                md={5.3}
                                sx={{
                                    borderRight: { xs: 'none', md: '1px solid #C4C4C4' },
                                    borderBottom: { xs: '1px solid #C4C4C4', md: 'none' },
                                }}
                            >
                                <Grid item xs={12} sx={{ px: { xs: 2, md: '20px' }, backgroundColor: 'white', pb: 2 }}>
                                    <Typography variant="body1" fontWeight={500} fontSize={'16px'} marginTop={'24px'}>
                                        Appointment Information
                                    </Typography>

                                    <Grid
                                        item
                                        xs={12}
                                        border={'1px solid #C4C4C4'}
                                        padding={'16px'}
                                        fontWeight={400}
                                        borderRadius={'4px'}
                                        fontSize={'16px'}
                                        marginTop={'7px'}
                                        width={'100%'}
                                    >
                                        <Grid container item>
                                            <Grid item xs={12} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <Typography variant="body1" fontWeight={400} fontSize={'16px'}>
                                                    Customer
                                                </Typography>
                                                <Typography variant="body1" fontWeight={400} fontSize={'16px'}>
                                                    <Link
                                                        style={{ textDecoration: 'none', cursor: 'pointer' }}
                                                        onClick={() => {
                                                            if (appointmentData?.customer?.createdFrom === USER_CREATED_FROM.SHOP_GUEST) {
                                                                setOpenGuestModal(true);
                                                            } else {
                                                                navigate(
                                                                    `/customers/${appointmentData?.customer?._id}/${appointmentData?.customer?.createdFrom}`
                                                                );
                                                            }
                                                        }}
                                                    >
                                                        {truncateText(
                                                            appointmentData?.customer?.firstName || appointmentData?.customer?.lastName
                                                                ? `${appointmentData?.customer?.firstName || ''} ${appointmentData?.customer?.lastName || ''}`.trim()
                                                                : '',
                                                            30
                                                        )}
                                                    </Link>
                                                </Typography>
                                            </Grid>
                                            <Divider
                                                style={{
                                                    padding: '5px',
                                                    width: '100%',
                                                    borderBottomStyle: 'solid',
                                                    borderBottomWidth: '2px',
                                                    marginBottom: '12px',
                                                }}
                                            />

                                            <Grid item xs={12} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <Typography variant="body1" fontWeight={400} fontSize={'16px'}>
                                                    Phone Number
                                                </Typography>
                                                <Typography variant="body1" fontWeight={400} fontSize={'16px'}>
                                                    {appointmentData?.customer?.phoneNumber && formatPhoneNumber(appointmentData.customer.phoneNumber)}
                                                </Typography>
                                            </Grid>

                                            {(tab === 3 || tab === 4) && (
                                                <Divider
                                                    style={{
                                                        padding: '5px',
                                                        width: '100%',
                                                        borderBottomStyle: 'solid',
                                                        borderBottomWidth: '2px',
                                                        marginBottom: '12px',
                                                    }}
                                                />
                                            )}
                                            {(tab === 3 || tab === 4) && (
                                                <Grid item xs={12} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                    <Typography variant="body1" fontWeight={400} fontSize={'16px'}>
                                                        Date Requested
                                                    </Typography>
                                                    <Typography variant="body1" fontWeight={400} fontSize={'16px'}>
                                                        {appointmentData?.createdAt && moment(appointmentData.createdAt).format('DD-MMM-YYYY hh:mm A')}
                                                    </Typography>
                                                </Grid>
                                            )}

                                            <Divider
                                                style={{
                                                    padding: '5px',
                                                    width: '100%',
                                                    borderBottomStyle: 'solid',
                                                    borderBottomWidth: '2px',
                                                    marginBottom: '12px',
                                                }}
                                            />

                                            <Grid item xs={12} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <Typography variant="body1" fontWeight={400} fontSize={'16px'}>
                                                    {showNew ? 'Old Drop Off' : 'Drop Off Date'}
                                                </Typography>
                                                <Typography variant="body1" fontWeight={400} fontSize={'16px'} color={'rgba(0, 0, 0, 0.87)'}>
                                                    {appointmentData?.dropOffDateTime && moment(appointmentData.dropOffDateTime).format('DD-MMM-YYYY hh:mm A')}
                                                </Typography>
                                            </Grid>
                                            <Divider
                                                style={{
                                                    padding: '5px',
                                                    width: '100%',
                                                    borderBottomStyle: 'solid',
                                                    borderBottomWidth: '2px',
                                                    marginBottom: '12px',
                                                }}
                                            />

                                            {showNew && (
                                                <>
                                                    <Grid item xs={12} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                        <Typography variant="body1" fontWeight={400} fontSize={'16px'}>
                                                            New Drop Off
                                                        </Typography>
                                                        <Typography variant="body1" fontWeight={400} fontSize={'16px'}>
                                                            {appointmentData?.proposedropOffDateTime &&
                                                                moment(appointmentData.proposedropOffDateTime).format('DD-MMM-YYYY hh:mm A')}
                                                        </Typography>
                                                    </Grid>
                                                    <Divider
                                                        style={{
                                                            padding: '5px',
                                                            width: '100%',
                                                            borderBottomStyle: 'solid',
                                                            borderBottomWidth: '2px',
                                                            marginBottom: '12px',
                                                        }}
                                                    />
                                                </>
                                            )}

                                            <Grid item xs={12} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <Typography variant="body1" fontWeight={400} fontSize={'16px'}>
                                                    Wait or Drop Off
                                                </Typography>
                                                <Typography variant="body1" fontWeight={400} fontSize={'16px'}>
                                                    {appointmentData?.waitingOnSite === 'dropping_off_car' ? 'Dropping Off Car' : 'Wait on Site'}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </Grid>

                                    <Typography variant="body1" fontWeight={500} fontSize={'16px'} marginTop={'24px'}>
                                        Contact Preferences
                                    </Typography>
                                    <Typography
                                        xs={12}
                                        border={'1px solid #C4C4C4'}
                                        padding={'16px'}
                                        fontWeight={400}
                                        borderRadius={'4px'}
                                        fontSize={'16px'}
                                        marginTop={'7px'}
                                        width={'100%'}
                                        display={'block'}
                                    >
                                        {appointmentData?.contactPreferences === 'phone'
                                            ? 'Phone Call'
                                            : appointmentData?.contactPreferences === 'email'
                                              ? 'Email'
                                              : 'In-App Message'}
                                    </Typography>

                                    {appointmentData?.noteForMechanic && (
                                        <>
                                            <Typography variant="body1" fontWeight={500} fontSize={'16px'} marginTop={'24px'}>
                                                Note from {appointmentData?.customer?.firstName}
                                            </Typography>
                                            <Typography
                                                xs={12}
                                                border={'1px solid #C4C4C4'}
                                                padding={'16px'}
                                                fontWeight={400}
                                                borderRadius={'4px'}
                                                fontSize={'16px'}
                                                marginTop={'7px'}
                                                width={'100%'}
                                                display={'block'}
                                                overflow={'auto'}
                                                maxHeight={'60px'}
                                                sx={{ scrollbarWidth: 'thin', whiteSpace: 'pre-line' }}
                                                marginBottom={'20px'}
                                            >
                                                {appointmentData.noteForMechanic}
                                            </Typography>
                                        </>
                                    )}
                                </Grid>
                            </Grid>

                            {/* ─── Right column ─── */}
                            <Grid container item xs={12} md={6.7}>
                                <div style={{ width: '100%' }}>
                                    <Grid item xs={12} sx={{ px: { xs: 2, md: '20px' }, pt: '24px' }}>
                                        <Typography variant="body1" fontWeight={500} fontSize={'16px'}>
                                            Vehicle Information
                                        </Typography>
                                        <Grid
                                            item
                                            xs={12}
                                            border={'1px solid #C4C4C4'}
                                            padding={'16px'}
                                            fontWeight={400}
                                            borderRadius={'4px'}
                                            fontSize={'16px'}
                                            marginTop={'7px'}
                                            width={'100%'}
                                            bgcolor={'white'}
                                        >
                                            <Grid container item>
                                                <Grid item xs={12} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                    <Typography variant="body1" fontWeight={400} fontSize={'16px'}>
                                                        Vehicle
                                                    </Typography>
                                                    <Typography variant="body1" fontWeight={400} fontSize={'16px'}>
                                                        {appointmentData?.vehicle?.year ? appointmentData.vehicle.year + ' ' : ' '}
                                                        {appointmentData?.vehicle?.vehicleMake?.name ? appointmentData.vehicle.vehicleMake.name + ' ' : ''}
                                                        {appointmentData?.vehicle?.vehicleModel?.name ? appointmentData.vehicle.vehicleModel.name : ''}
                                                    </Typography>
                                                </Grid>
                                                <Divider
                                                    style={{
                                                        padding: '5px',
                                                        width: '100%',
                                                        borderBottomStyle: 'solid',
                                                        borderBottomWidth: '2px',
                                                        marginBottom: '12px',
                                                    }}
                                                />

                                                <Grid item xs={12} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                    <Typography variant="body1" fontWeight={400} fontSize={'16px'}>
                                                        Trim
                                                    </Typography>
                                                    <Typography variant="body1" fontWeight={400} fontSize={'16px'}>
                                                        {appointmentData?.vehicle?.vehicleModelTrim?.name ?? ' - '}
                                                    </Typography>
                                                </Grid>
                                                <Divider
                                                    style={{
                                                        padding: '5px',
                                                        width: '100%',
                                                        borderBottomStyle: 'solid',
                                                        borderBottomWidth: '2px',
                                                        marginBottom: '12px',
                                                    }}
                                                />

                                                <Grid item xs={12} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                    <Typography variant="body1" fontWeight={400} fontSize={'16px'}>
                                                        VIN
                                                    </Typography>
                                                    <Typography variant="body1" fontWeight={400} fontSize={'16px'}>
                                                        {appointmentData?.vehicle?.vin ?? ' - '}
                                                    </Typography>
                                                </Grid>
                                                <Divider
                                                    style={{
                                                        padding: '5px ',
                                                        width: '100%',
                                                        borderBottomStyle: 'solid',
                                                        borderBottomWidth: '2px',
                                                        marginBottom: '12px',
                                                    }}
                                                />

                                                <Grid
                                                    item
                                                    lg={12}
                                                    md={12}
                                                    sm={12}
                                                    xs={12}
                                                    style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                                                >
                                                    <Typography variant="body1" fontWeight={400} fontSize={'16px'}>
                                                        Licence Plate
                                                    </Typography>
                                                    <Typography variant="body1" fontWeight={400} fontSize={'16px'}>
                                                        {appointmentData?.vehicle?.province?.provinceCode
                                                            ? `${appointmentData?.vehicle?.province?.provinceCode} ${appointmentData?.vehicle?.licenseNumber || ''}`
                                                            : appointmentData?.vehicle?.licenseNumber || '-'}
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Grid>

                                    <Grid item xs={12} sx={{ px: { xs: 2, md: '20px' }, pt: '24px', pb: '24px' }}>
                                        <Typography variant="body1" fontWeight={500} fontSize={'16px'}>
                                            Services
                                        </Typography>
                                        <Grid
                                            item
                                            xs={12}
                                            border={'1px solid #C4C4C4'}
                                            padding={'16px'}
                                            fontWeight={400}
                                            borderRadius={'4px'}
                                            fontSize={'16px'}
                                            marginTop={'7px'}
                                            width={'100%'}
                                            bgcolor={'white'}
                                            maxHeight={'40vh'}
                                            overflow={'auto'}
                                            sx={{ scrollbarWidth: 'thin' }}
                                        >
                                            <Grid container item>
                                                {appointmentData?.services?.map((item, index) => (
                                                    <React.Fragment key={item?._id || item?.name}>
                                                        {index !== 0 && (
                                                            <Divider
                                                                style={{
                                                                    padding: '5px ',
                                                                    width: '100%',
                                                                    borderBottomStyle: 'solid',
                                                                    borderBottomWidth: '2px',
                                                                    marginBottom: '12px',
                                                                }}
                                                            />
                                                        )}
                                                        <Grid item xs={12} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                            <Typography variant="body1" fontWeight={400} fontSize={'16px'}>
                                                                {item?.name || item?.serviceName}
                                                            </Typography>
                                                            <Typography variant="body1" style={{ fontWeight: 400, fontSize: '16px' }}>
                                                                {item?.pricingType === 'no_price' ? (
                                                                    'No Price'
                                                                ) : item?.pricingType === 'fixed' ? (
                                                                    item?.fixedPrice && <FormatNumber inputValue={item.fixedPrice} />
                                                                ) : item?.pricingType === 'range' ? (
                                                                    <>
                                                                        <FormatNumber inputValue={item?.priceMin} /> -{' '}
                                                                        <FormatNumber inputValue={item?.priceMax} />
                                                                    </>
                                                                ) : (
                                                                    ''
                                                                )}
                                                            </Typography>
                                                        </Grid>
                                                        {item?.questionnaire?.map((row) => (
                                                            <React.Fragment key={row?.question}>
                                                                <Grid item xs={12}>
                                                                    <Typography style={{ fontSize: '14px', fontWeight: 400, marginTop: '5px' }} variant="body1">
                                                                        {row?.question}
                                                                    </Typography>
                                                                </Grid>
                                                                <Grid item xs={12}>
                                                                    <Typography
                                                                        style={{ fontSize: '14px', fontWeight: 400, marginTop: '5px', marginBottom: '5px' }}
                                                                        variant="body1"
                                                                    >
                                                                        {row?.answer}
                                                                    </Typography>
                                                                </Grid>
                                                            </React.Fragment>
                                                        ))}
                                                    </React.Fragment>
                                                ))}
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </div>
                            </Grid>
                        </Grid>
                    </Container>
                </DialogContent>

                {/* ── DialogActions is the fixed footer with buttons ── */}
                {isProposeTime && (
                    <DialogActions
                        sx={{
                            flexShrink: 0,
                            backgroundColor: '#ffffff',
                            borderTop: '1px solid #C4C4C4',
                            px: { xs: 2, md: '20px' },
                            py: '16px',
                            justifyContent: 'flex-end',
                            gap: '12px',
                            flexWrap: { xs: 'wrap', sm: 'nowrap' },
                        }}
                    >
                        {showDeclineBtn && (
                            <Button variant="contained" color="error" onClick={handleDecline}>
                                DECLINE
                            </Button>
                        )}
                        {showProposeBtn && (
                            <Button variant="contained" color="primary" onClick={handleProposeTime}>
                                PROPOSE NEW TIME
                            </Button>
                        )}
                        {showAcceptBtn && (
                            <Button variant="contained" color="primary" onClick={handleAccept}>
                                ACCEPT
                            </Button>
                        )}
                    </DialogActions>
                )}
            </Dialog>

            <RequestProposeTimeModel
                shopEndTime={appointmentData?.shopClosingTime}
                open={showModel}
                handleClose={handleClosePropose}
                id={appointmentData?._id}
                handleClose2={handleClose}
                apiCall={apiCall}
            />
            <AddAssigneeModal
                open={showAssignee}
                handleClose={handleCloseAssigneeModal}
                id={appointmentData?._id}
                handleCloseRequestModal={handleClose}
                apiCall={apiCall}
            />
            <EditEstimatePickUpModal
                open={showEstimatePickUp}
                handleClose={handleCloseEsatimatePickUpModal}
                setEstimatePickUpDate={setEstimatePickUpDate}
                id={appointmentData?._id}
                dropOffDate={appointmentData?.dropOffDateTime}
            />
            <GuestCustomerInfoModal
                userData={appointmentData}
                openGuestModal={openGuestModal}
                setOpenGuestModal={setOpenGuestModal}
                fetchAppointmentDetails={fetchAppointmentDetails}
            />

            <SuccessMessageModal message={successMessage} open={openSuccessModal} setOpenSuccessModal={setOpenSuccessModal} />

            <Dialog fullScreen={false} open={showLoader}>
                <DialogContent>
                    <CircularProgress />
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default AppointmentRequestModal;
